<template>
  <div>
    <template v-for="(submenu, index) in submenus" :key="index">
      <div v-if="isSubmenuVisible(submenu)" class="card shadow mb-2">
      <div class="card-body p-sm-0">
        <h4 class="card-title mb-0">{{ submenu.name }}</h4>
        <hr>
      </div>
      <div class="card-body card-pd py-0">
        <div class="row text-center dashboard-card" style="margin: 5px auto;">
          <template v-for="(page, pageIndex) in submenu.pages" :key="getStatItem(page.route)">
            <component
              v-if="page.status === 'active' && (page.card_type === 'input' || page.card_type === 'list' || (statisticsItem && statisticsItem[getStatItem(page.route)]))"
              :is="cards[getComponentKey(page)]"
              :page="page"
              :itemKey="getStatItem(page.route)"
              :statisticsItem="statisticsItem"
              @onDateChange="onDateChange"
              @onTypeChange="onTypeChange"
              :timeRange="timeRange"
            />
          </template>
        </div>
      </div>
    </div>
    </template>
    
    <div v-if="(!statisticsItem || Object.keys(statisticsItem).length === 0) && !hasInputOrListCards" class="card shadow p-5 text-center mt-2 w-100">
      <h4 class="text-muted mb-0"><i class="feather icon-info mr-1"></i> Please Sync Dashboard First</h4>
    </div>
  </div>
</template>

<script setup>
import {useRoute} from "vue-router";
import {onMounted, ref, computed} from "vue";
import {useCardStatistics} from "@/services/modules/dashboard/cardStatistics";
import {
  CardTotalType,
  CardBarChart,
  CardPieChart,
  CardSummary,
  CardHosTotal,
  CardHosSummary,
  CardHosBarChart,
  AttendanceList,
  Attendance,
  CardLeaveSchedule,
  CardLeaveApproval,
  CardProjectActivityGroupTva,
  CardProjectActivityTopAchieve,
  CardProjectActivityLowAchieve,
  CardProjectActivityDonorKpi,
  CardTimesheetSummary
} from '@/components/molecule/company/dashboard/dynamic-dashboard/components';

const cards = {
  'card_total_value': CardTotalType,
  'card_bar_chart': CardBarChart,
  'card_pie_chart': CardPieChart,
  'card_summary': CardSummary,
  'card_hos_total': CardHosTotal,
  'card_hos_summary': CardHosSummary,
  'card_hos_bar_chart': CardHosBarChart,
  'card_attendance_list': AttendanceList,
  'card_attendance': Attendance,
  'card_leave_schedule': CardLeaveSchedule,
  'card_leave_approval': CardLeaveApproval,
  'card_project_activity_group_tva': CardProjectActivityGroupTva,
  'card_project_activity_top_achieve': CardProjectActivityTopAchieve,
  'card_project_activity_low_achieve': CardProjectActivityLowAchieve,
  'card_project_activity_donor_kpi': CardProjectActivityDonorKpi,
  'card_timesheet_summary': CardTimesheetSummary,
};

const props = defineProps({
  submenus: Array,
})

const getComponentKey = (page) => {
  const formattedRoute = getStatItem(page.route);
  if (page.card_type === 'input' || page.card_type === 'list') {
    return "card_" + formattedRoute;
  }
  return statisticsItem.value[formattedRoute].dashboard_type;
};

const hasInputOrListCards = computed(() => {
  return props.submenus.some(submenu => 
    submenu.pages.some(page => 
      page.status === 'active' && (page.card_type === 'input' || page.card_type === 'list')
    )
  )
})

const isSubmenuVisible = (submenu) => {
  return submenu.pages.some(page => 
    page.status === 'active' && (
      page.card_type === 'input' || 
      page.card_type === 'list' || 
      (statisticsItem.value && statisticsItem.value[getStatItem(page.route)])
    )
  )
}

const emit = defineEmits(['updated'])

const extractAndEmitLastUpdated = (data) => {
  if (data) {
    const firstDataKey = Object.keys(data)[0]
    if (firstDataKey && data[firstDataKey] && data[firstDataKey].calculated_at) {
      emit('updated', data[firstDataKey].calculated_at)
    }
  }
}

onMounted(async () => {
  const initialData = await getDashboardSummariesFromCache()
  extractAndEmitLastUpdated(initialData)
  
  // Listen for reload event from sync button
  window.addEventListener('reload-dashboard-summaries', handleReloadSummaries)
})

const handleReloadSummaries = async (event) => {
  const shouldFilter = event.detail && event.detail.company_ids && event.detail.company_ids.length
  let companyIds = [];
  let filterQuery = '';

  if (shouldFilter) {
    const companyIds = event.detail.company_ids.map(company => company.id);

    const params = new URLSearchParams();

    companyIds.forEach(id => {
      params.append('company_ids[]', id);
    });

    filterQuery = `&${params.toString()}`;
  }

  const reloadedData = await getDashboardSummaries(filterQuery, shouldFilter)
  extractAndEmitLastUpdated(reloadedData)
}

const getStatItem = (routeKey) => {
  return routeKey.replace(/-/g, '_')
}

const route = useRoute()
const params = route.params

const onDateChange = (item) => {
  const query = `&dashboard_key=${item.dashboard_key}&period_type=${item.period_type}`;
  getDashboardSummaryByKey(query, item.dashboard_key);
}

const onTypeChange = (item, type) => {
  const query = `&dashboard_key=${item.dashboard_key}&period_type=${item.period_type}&type=${type}`;
  getDashboardSummaryByKey(query, item.dashboard_key);
};

defineExpose({
  onDateChange,
  onTypeChange
});

const {
  timeRange,
  getDashboardSummaries,
  getDashboardSummariesFromCache,
  getDashboardSummaryByKey,
  statisticsItem
} = useCardStatistics(params)
</script>

<style scoped>

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-color: #ffffff;
  background-clip: border-box;
  border: 0 solid rgba(34, 41, 47, 0.125);
  border-radius: 15px;
}

.card-body {
  /*padding: 0;*/
  border-radius: 15px 15px 0 0 !important;
}

.card-title {
  background-color: #fff;
  margin: 0;
  padding: 15px 15px;
  color: #7D7D7D;
  border-radius: 15px 15px 0 0;
  /* border-bottom: .5px solid #7D7D7D; */
}

.card-pd {
  padding-left: 5px;
  padding-right: 5px;
}

.pd-5 {
  padding: 5px;
}

hr {
  margin: 0;
}

/deep/ .dashboard-card .card, /deep/ .dashboard-card .form-control {
  background-color: #ebecf0;
}

/deep/ .dashboard-card .card.height-235 {
  height: 235px;
}

/deep/ .dashboard-card .card .cut-text {
  width: 143px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

</style>
